import { CMSData } from "@/cms/models";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import BranchOrganisationIcon from "../BranchOrganisationIcon";
import { formatPhoneNumber, mapSocialIcon } from "@/utils/helper";
import CookieSettingsLink from "@/components/CookieSettingsLink";
import Link from "next/link";
import { ApplicationFraction } from "@/utils/gql/graphql";
import DefaultPartner from "@/components/layouts/default/DefaultPartner";

const DefaultFooterComponent: React.FC<{
  config: {
    app: ApplicationFraction;
    cmsData: CMSData;
    links: {
      title: string;
      url: string;
    }[];
  };
}> = ({ config }) => {
  const { app, cmsData, links } = config;
  const appData = app;

  if (!appData.resourceGroup) {
    return null;
  }

  const [addressStreetName] =
    appData.resourceGroup.address?.displayName?.split(",") ?? [];
  const postalCode = appData.resourceGroup.address?.zipCode;
  const cityName = appData.resourceGroup.address?.cityName;

  const rgHeadline = appData.resourceGroup.marketing?.homepage?.headline;
  const rgDescription = appData.resourceGroup.marketing?.homepage?.description;

  return (
    <footer className="homepage-text-sm p-4 md:pt-10 z-10 flex justify-center text-[var(--footer-text-color)] bg-[var(--footer-bg-color)]  ">
      <div className="max-w-screen-2xl w-full flex flex-col">
        <div className="flex-col gap-4 md:flex-row flex md:grid grid-cols-3 md:gap-16 flex-1 text-[var(--footer-text-color)] ">
          <div className="flex flex-col flex-1 gap-4">
            <div className="homepage-text-sm">
              {appData.resourceGroup.displayName}
            </div>
            {appData.resourceGroup.address && (
              <div className="flex flex-col">
                <span>{addressStreetName}</span>
                <span>
                  {postalCode} {cityName}
                </span>
              </div>
            )}
            <div className="flex flex-col">
              {appData.resourceGroup.phone?.internationalNumber && (
                <Link
                  aria-label="Phone"
                  className="block"
                  href={`tel:${appData.resourceGroup.phone?.internationalNumber}`}
                >
                  {formatPhoneNumber(
                    appData.resourceGroup.phone?.prefix ?? "",
                    appData.resourceGroup.phone?.number ?? ""
                  )}
                </Link>
              )}

              {appData.resourceGroup.email?.address && (
                <Link
                  aria-label="Email"
                  className="block"
                  href={`mailto:${appData.resourceGroup.email?.address}`}
                >
                  {appData.resourceGroup.email?.address}
                </Link>
              )}
            </div>
            {appData.resourceGroup.cvr && (
              <div>CVR {appData.resourceGroup.cvr}</div>
            )}
          </div>
          <div className="flex flex-col flex-1 gap-4">
            <span className="homepage-text-sm">Åbningstider</span>
            <div>
              {[
                {
                  label: "Mandag",
                  value: appData.resourceGroup.openingHours?.monday,
                },
                {
                  label: "Tirsdag",
                  value: appData.resourceGroup.openingHours?.tuesday,
                },
                {
                  label: "Onsdag",
                  value: appData.resourceGroup.openingHours?.wednesday,
                },
                {
                  label: "Torsdag",
                  value: appData.resourceGroup.openingHours?.thursday,
                },
                {
                  label: "Fredag",
                  value: appData.resourceGroup.openingHours?.friday,
                },
                {
                  label: "Lørdag",
                  value: appData.resourceGroup.openingHours?.saturday,
                },
                {
                  label: "Søndag",
                  value: appData.resourceGroup.openingHours?.sunday,
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex justify-between capitalize"
                >
                  <span>{item.label}</span>
                  <span>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col flex-1 gap-4">
            {(rgHeadline || rgDescription) && (
              <div className="flex flex-col gap-2">
                <span className="homepage-text-sm">{rgHeadline}</span>
                <div
                  dangerouslySetInnerHTML={{
                    __html: rgDescription ?? "",
                  }}
                ></div>
              </div>
            )}
            {appData.resourceGroup.soMes.some(
              (item) => item.namespace !== "some.www"
            ) && (
              <div className="flex gap-2">
                {appData.resourceGroup.soMes
                  .filter((item) => item.namespace !== "some.www")
                  .map((item: any) => (
                    <a
                      key={item.namespace}
                      href={
                        item.url.startsWith("https://") ||
                        item.url.startsWith("mailto:")
                          ? item.url
                          : `https://${item.url}`
                      }
                      aria-label={item.namespace}
                      className="p-2 rounded-full border-2 flex border-[var(--footer-text-color)] "
                      target="_blank"
                    >
                      {item.namespace != "some.trustpilot" && (
                        <FontAwesomeIcon
                          className="size-4 flex"
                          icon={mapSocialIcon(item.namespace)}
                        />
                      )}
                      {item.namespace == "some.trustpilot" && (
                        <svg
                          className="size-4 flex"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          role="img"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <title>Trustpilot icon</title>
                          <path d="M12,17.964l5.214-1.321l2.179,6.714L12,17.964z M24,9.286h-9.179L12,0.643L9.179,9.286 H0l7.429,5.357l-2.821,8.643l7.429-5.357l4.571-3.286L24,9.286L24,9.286L24,9.286L24,9.286z" />
                        </svg>
                      )}
                    </a>
                  ))}
              </div>
            )}
            {appData.resourceGroup.branchOrganisations &&
              appData.resourceGroup.branchOrganisations.length > 0 && (
                <div className="flex flex-col gap-4">
                  <span className="homepage-text-sm">Medlem af:</span>
                  <div className="flex gap-2 color-[var(--footer-text-color)]">
                    {appData.resourceGroup.branchOrganisations?.map(
                      (item, index) => (
                        <BranchOrganisationIcon
                          key={index}
                          branchOrganisation={item}
                        />
                      )
                    )}
                  </div>
                </div>
              )}
            <div className="flex flex-col gap-1">
              {!cmsData?.googleTagManagerId && <CookieSettingsLink />}
              {links?.map((item, index) => (
                <Link
                  key={index}
                  href={item.url}
                  className="cursor-pointer hover:underline"
                >
                  {item.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
        <DefaultPartner
          app={{
            appData,
            cmsData,
          }}
        />
      </div>
    </footer>
  );
};

export default DefaultFooterComponent;
